/**
 * LPS Scroll Banner – scroll-driven frame animation.
 * Finds all [data-lps-scroll-banner] and drives each from plain scroll
 * position — no animation library. When pinning is enabled the section
 * markup includes a tall ".lps-scroll-banner__track" with a
 * "position: sticky" child (see LPS-scroll-banner.liquid); the browser
 * itself keeps that sticky correctly positioned as the page's layout
 * changes (lazy images, webfonts, other sections resizing, …), so unlike a
 * JS-computed pin there is nothing here that can go stale and needs a
 * manual refresh.
 */
(function() {
  var MOBILE_BREAKPOINT = 750;
  var scrollUpdaters = [];
  var resizeUpdaters = [];
  var ticking = false;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // The theme sticks its header above everything else and exposes its
  // height as --header-height on <body> (see layout/theme.liquid) so other
  // sticky elements can offset below it instead of hiding underneath it —
  // same convention product-information-content.liquid uses.
  function getHeaderOffset() {
    var v = getComputedStyle(document.body).getPropertyValue('--header-height');
    var n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  function requestScrollTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function() {
      for (var i = 0; i < scrollUpdaters.length; i++) scrollUpdaters[i]();
      ticking = false;
    });
  }

  function runResizeUpdaters() {
    for (var i = 0; i < resizeUpdaters.length; i++) resizeUpdaters[i]();
  }

  function initSection(wrapper) {
    if (wrapper.hasAttribute('data-lps-scroll-banner-inited')) return;
    wrapper.setAttribute('data-lps-scroll-banner-inited', '1');
    var canvas = wrapper.querySelector('canvas');
    if (!canvas) return;

    var isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile && wrapper.getAttribute('data-hide-mobile') === 'true') return;

    var frameRoot = isMobile && wrapper.getAttribute('data-frame-root-mobile')
      ? wrapper.getAttribute('data-frame-root-mobile')
      : wrapper.getAttribute('data-frame-root');
    var frameCount = parseInt(
      isMobile && wrapper.getAttribute('data-frame-count-mobile')
        ? wrapper.getAttribute('data-frame-count-mobile')
        : wrapper.getAttribute('data-frame-count'),
      10
    );
    if (!frameRoot || frameCount < 1) return;

    var pin = wrapper.getAttribute('data-pin') === 'true';
    var track = pin ? wrapper.querySelector('.lps-scroll-banner__track') : null;
    var sticky = pin ? wrapper.querySelector('.lps-scroll-banner__sticky') : null;
    var ctx = canvas.getContext('2d');
    var images = [];
    var currentFrame = -1;
    var targetFrame = 0;

    function setCanvasSize(naturalWidth, naturalHeight) {
      canvas.width = naturalWidth;
      canvas.height = naturalHeight;
    }

    function drawFrame(index) {
      var img = images[index];
      if (!img || !img.complete || !img.naturalWidth || !canvas.width || !canvas.height) return;
      if (index === currentFrame) return;
      currentFrame = index;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    }

    function onFrameLoaded(index) {
      return function() {
        if (index === 0) {
          var w = this.naturalWidth;
          var h = this.naturalHeight;
          if (w && h) {
            setCanvasSize(w, h);
          } else {
            var fallbackW = parseInt(isMobile && wrapper.getAttribute('data-width-mobile') ? wrapper.getAttribute('data-width-mobile') : wrapper.getAttribute('data-width'), 10) || 1920;
            var fallbackH = parseInt(isMobile && wrapper.getAttribute('data-height-mobile') ? wrapper.getAttribute('data-height-mobile') : wrapper.getAttribute('data-height'), 10) || 1080;
            setCanvasSize(fallbackW, fallbackH);
          }
        }
        if (index === 0) syncMobileTrackHeight();
        // Repaint as soon as this frame arrives if it's the one currently
        // wanted — without this, a frame that finishes loading after the
        // visitor has scrolled past it never gets drawn.
        if (index === targetFrame) drawFrame(index);
      };
    }

    for (var i = 0; i < frameCount; i++) {
      var img = new Image();
      img.onload = onFrameLoaded(i);
      img.src = frameRoot + String(i).padStart(3, '0') + '.jpg';
      images.push(img);
    }

    // On mobile the sticky content shrinks to fit the image (see
    // LPS-custom.css) rather than filling the screen, so a scroll runway
    // sized in viewport-heights (built for a near-fullscreen desktop pin)
    // is wildly too long relative to it — that's what shows up as a big
    // empty gap while scrolling. Size the runway off the actual pinned
    // content instead: a few multiples of its own height.
    var MOBILE_TRACK_MULTIPLIER = 3;
    function syncMobileTrackHeight() {
      if (!isMobile || !pin || !track || !sticky) return;
      var h = sticky.getBoundingClientRect().height;
      if (h > 0) track.style.height = (h * MOBILE_TRACK_MULTIPLIER) + 'px';
    }

    function update() {
      var vh = window.innerHeight;
      var progress;
      if (pin && track) {
        // Use the sticky element's own (CSS-controlled, can differ by
        // breakpoint — see LPS-custom.css) height rather than assuming it
        // fills the viewport, so the frame math stays correct whatever
        // that height is.
        var stickyHeight = sticky ? sticky.getBoundingClientRect().height : vh;
        var headerOffset = getHeaderOffset();
        var rect = track.getBoundingClientRect();
        var scrollable = rect.height - stickyHeight;
        if (scrollable <= 0) {
          progress = rect.top <= headerOffset ? 1 : 0;
        } else {
          // The sticky element clamps at `top: headerOffset`, not 0 — see
          // LPS-custom.css — so progress needs to track scroll relative to
          // that clamp point, not the viewport's raw top edge.
          progress = (headerOffset - rect.top) / scrollable;
        }
      } else {
        var wrect = wrapper.getBoundingClientRect();
        var total = vh + wrect.height;
        progress = total > 0 ? (vh - wrect.top) / total : 0;
      }
      progress = Math.max(0, Math.min(1, progress));
      targetFrame = Math.round(progress * (frameCount - 1));
      drawFrame(targetFrame);
    }

    function resizeSync() {
      syncMobileTrackHeight();
      update();
    }

    scrollUpdaters.push(update);
    resizeUpdaters.push(resizeSync);
    resizeSync();
  }

  ready(function() {
    var els = document.querySelectorAll('[data-lps-scroll-banner]');
    for (var i = 0; i < els.length; i++) initSection(els[i]);
    if (!scrollUpdaters.length) return;
    window.addEventListener('scroll', requestScrollTick, { passive: true });
    window.addEventListener('resize', runResizeUpdaters);
    window.addEventListener('load', runResizeUpdaters);
  });
})();
